import { Grid, Stack, Typography, Box } from "@mui/material";
import { PresentationHeader } from "./PresentationHeader";
import { DataPresentation } from "./DataPresentation";
import { useFormatter } from "../../../utils/useFormatter";
import dayjs from "dayjs";
import { SubmitActions } from "./SubmitActions";
import { AttendanceType, ATTENDANCE_TYPE_LABEL } from "../types";

export function ConfirmationScreen({
  location,
  healthcare,
  schedule,
  setCurrentStep,
  onNext,
  loading,
  attendanceType,
}: any) {
  const address = [
    location?.city && `${location?.city}`,
    location?.state && `${location?.state}`,
    location?.cep && `${location?.cep}`,
    location?.number && `${location?.number}`,
    location?.street && `${location?.street}`,
    location?.complement && `${location?.complement}`,
  ];

  // Proteção: timeAvailable pode estar ausente quando vindo de fluxos sem step de healthcare
  const auxiliarTimeAvailable: any = healthcare?.timeAvailable
    ? Object?.values(healthcare.timeAvailable)
    : [];

  const formatAntecedencyDays = (day: number): string => {
    const daysFormatter = [
      { display: "1 semana", value: 7 },
      { display: "2 semanas", value: 14 },
      { display: "3 semanas", value: 21 },
      { display: "1 mês", value: 30 },
      { display: "2 meses", value: 60 },
      { display: "3 meses", value: 90 },
      { display: "6 meses", value: 180 },
      { display: "Agenda completa", value: day },
    ].find((element) => element.value === day);
    return daysFormatter?.display || "";
  };

  const formatTimeDefault = (): string => {
    return schedule?.durationTimeDefault === "personDuration"
      ? dayjs(schedule?.personDurationTime).format("HH:mm")
      : schedule?.durationTimeDefault;
  };

  // Mesmo critério do ScheduleSettings (FormRadioButton scheduleExposureEnabled):
  // true = pública em portais; false/undefined = privada (uso interno).
  const scheduleExposureLabel =
    schedule?.scheduleExposureEnabled === true ||
    schedule?.scheduleExposureEnabled === "true"
      ? "Agenda pública (exposta em portais)"
      : "Agenda privada (apenas uso interno)";

  return (
    <Stack width="100%" mb={2}>
      <Typography
        variant="h6"
        fontWeight="600"
        textAlign="center"
        color="neutral700.main"
        paddingY={2} // Espaçamento do topo
        mb={2}
      >
        Resumo das configurações criadas para a agenda
      </Typography>

      <Box>
        <PresentationHeader order="1" text="Localização e Tipo de Atendimento" />
        <Grid container justifyContent="space-between" m="16px 40px 24px 40px" rowSpacing={2}>
          {/* Tipo de atendimento escolhido */}
          {attendanceType && (
            <Grid item xs={6}>
              <DataPresentation
                header="Tipo de atendimento"
                value={ATTENDANCE_TYPE_LABEL[attendanceType as AttendanceType] || attendanceType}
              />
            </Grid>
          )}

          {/* Nome da localização */}
          <Grid item xs={6}>
            <DataPresentation header="Nome da localização" value={`${location?.name || "-"}`} />
          </Grid>

          {/* Endereço: apenas para presencial */}
          {!location?.serviceOnline && attendanceType === "presencial" && (
            <Grid item xs={6}>
              <DataPresentation
                header="Endereço"
                value={`${address?.filter((el: any) => el?.length)?.join(", ") || "-"}`}
              />
            </Grid>
          )}

          {/* Informativo para domicílio */}
          {attendanceType === "domicilio" && (
            <Grid item xs={12}>
              <DataPresentation
                header="Endereço"
                value="A ser configurado posteriormente"
              />
            </Grid>
          )}
        </Grid>
      </Box>

      <Box>
        <PresentationHeader order="2" text="Serviço de saúde" />
        <Grid
          container
          justifyContent="space-between"
          m="16px 40px 24px 40px"
          rowSpacing={2}
        >
          <Grid item xs={6}>
            <DataPresentation
              header="Serviços de saúde"
              value={`${healthcare.healthService}`}
            />
            <Stack mt={3}>
              <DataPresentation
                header="Dias disponíveis na semana"
                value={`${healthcare.daysOfWeek}`}
              />
            </Stack>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" fontWeight={600} fontSize={14} color="primary800.main" mb={2}>
              Horários disponíveis na semana
            </Typography>
            <Stack direction="column" width="100%" spacing={1}>
              {healthcare.daysOfWeek.map((el: any, index: number) => (
                <Grid container key={`${el}.${index}`} mb={1}>
                  <Grid item xs={2}>
                    <Typography
                      variant="body1"
                      color="text.primary"
                      fontWeight={600}
                    >
                      {`${el}:`}
                    </Typography>
                  </Grid>

                  {auxiliarTimeAvailable?.map(
                    (auxTime: any, auxIndex: number) => {
                      return (
                        <Grid item xs={3} key={`${auxTime}.${auxIndex}`}>
                          <Stack direction="row" spacing={2}>
                            <Typography variant="body1" fontWeight={500} color="text.primary">
                              {useFormatter.formatTime(auxTime[el]?.timeStart)} -
                              {useFormatter.formatTime(auxTime[el]?.timeEnd)}
                            </Typography>
                          </Stack>
                        </Grid>
                      );
                    }
                  )}
                </Grid>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <Box>
        <PresentationHeader order="3" text="Configurações" />
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          m="16px 40px 24px 40px"
          rowSpacing={1}
        >
          <Grid item xs={6}>
            <DataPresentation
              header="Nome da agenda"
              value={`${schedule?.scheduleName}`}
            />
          </Grid>

          <Grid item xs={6}>
            <DataPresentation
              header="Duração padrão de cada consulta"
              value={formatTimeDefault()}
            />
          </Grid>

          <Grid item xs={6}>
            <DataPresentation
              header="Exposição pública da agenda"
              value={scheduleExposureLabel}
            />
          </Grid>

          {schedule?.returnDays ? (
            <Grid item xs={6}>
              <DataPresentation
                header="Dias para retorno de consulta"
                value={`${schedule?.returnDays} dias`}
              />
            </Grid>
          ) : (
            <></>
          )}

          {schedule?.advanceWarning ? (
            <Grid item xs={6}>
              <DataPresentation
                header="Antecedência de liberação de vagas"
                value={`${formatAntecedencyDays(schedule?.advanceWarning)}`}
              />
            </Grid>
          ) : (
            <> </>
          )}
          {healthcare?.professionalActor || schedule?.professionalActor ? (
            <Grid item xs={6}>
              <Typography variant="body2" fontWeight={600} fontSize={14} color="primary800.main" mb={1}>
                Profissional associado a essa agenda
              </Typography>

              <Typography variant="body1" color="text.primary" fontWeight={500}>
                {(healthcare?.professionalActor || schedule?.professionalActor)
                  ?.label}
              </Typography>
            </Grid>
          ) : (
            <></>
          )}

          <Grid item xs={6}>
            <DataPresentation
              header="Duração da agenda"
              value={`${dayjs(schedule?.dataStart).format(
                "DD/MM/YYYY"
              )} até ${dayjs(schedule?.dataEnd).format("DD/MM/YYYY")}`}
            />
          </Grid>
          {schedule?.serviceCost ? (
            <Grid item xs={6}>
              <DataPresentation
                header="Valor da consulta"
                value={`${schedule?.serviceCost}`}
              />
            </Grid>
          ) : (
            <></>
          )}
        </Grid>
      </Box>

      <Box sx={{ mt: 4 }}>
        <SubmitActions
          goBack={() => {
            setCurrentStep((prev: number) => prev - 1);
          }}
          goForward={async () => {
            await onNext();
            setCurrentStep((prev: number) => prev + 1);
          }}
          loading={loading}
        />
      </Box>
    </Stack>
  );
}
